import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PermissionKey } from '@pm/contracts';
import { AccessProvider } from './access.js';
import { api } from './api/client.js';
import { Layout } from './components/Layout.js';
import { actionPermissions } from './components/navigationPermissions.js';
import { LoginPage } from './pages/LoginPage.js';
import { sessionResponseSchema, type Session } from './features/auth/schemas.js';

const ResourcesPage = lazy(() =>
  import('./pages/ResourcesPage.js').then(({ ResourcesPage }) => ({ default: ResourcesPage })),
);
const SquadsPage = lazy(() =>
  import('./pages/SquadsPage.js').then(({ SquadsPage }) => ({ default: SquadsPage })),
);
const SquadsSettingsPage = lazy(() =>
  import('./pages/SquadsSettingsPage.js').then(({ SquadsSettingsPage }) => ({
    default: SquadsSettingsPage,
  })),
);
const ResourceProfilePage = lazy(() =>
  import('./pages/ResourceProfilePage.js').then(({ ResourceProfilePage }) => ({
    default: ResourceProfilePage,
  })),
);
const BillingPage = lazy(() =>
  import('./pages/BillingPage.js').then(({ BillingPage }) => ({ default: BillingPage })),
);
const BudgetsPage = lazy(() =>
  import('./pages/BudgetsPage.js').then(({ BudgetsPage }) => ({ default: BudgetsPage })),
);
const CapacityCalendarPage = lazy(() =>
  import('./pages/CapacityCalendarPage.js').then(({ CapacityCalendarPage }) => ({
    default: CapacityCalendarPage,
  })),
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage.js').then(({ DashboardPage }) => ({ default: DashboardPage })),
);
const GovernancePage = lazy(() =>
  import('./pages/GovernancePage.js').then(({ GovernancePage }) => ({ default: GovernancePage })),
);
const ForecastSettingsPage = lazy(() =>
  import('./pages/ForecastSettingsPage.js').then(({ ForecastSettingsPage }) => ({
    default: ForecastSettingsPage,
  })),
);
const FinancialRatesPage = lazy(() =>
  import('./pages/FinancialRatesPage.js').then(({ FinancialRatesPage }) => ({
    default: FinancialRatesPage,
  })),
);
const FinancialForecastPage = lazy(() =>
  import('./pages/FinancialForecastPage.js').then(({ FinancialForecastPage }) => ({
    default: FinancialForecastPage,
  })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/IntegrationsPage.js').then(({ IntegrationsPage }) => ({
    default: IntegrationsPage,
  })),
);
const InsightsPage = lazy(() =>
  import('./pages/InsightsPage.js').then(({ InsightsPage }) => ({ default: InsightsPage })),
);
const PlanningPage = lazy(() =>
  import('./pages/PlanningPage.js').then(({ PlanningPage }) => ({ default: PlanningPage })),
);
const ProjectsPage = lazy(() =>
  import('./pages/ProjectsPage.js').then(({ ProjectsPage }) => ({ default: ProjectsPage })),
);
const ProjectWorkspacePage = lazy(() =>
  import('./pages/ProjectWorkspacePage.js').then(({ ProjectWorkspacePage }) => ({
    default: ProjectWorkspacePage,
  })),
);
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage.js').then(({ ReportsPage }) => ({ default: ReportsPage })),
);
const SettingsClientsPage = lazy(() =>
  import('./pages/SettingsClientsPage.js').then(({ SettingsClientsPage }) => ({
    default: SettingsClientsPage,
  })),
);
const ContractSettingsPage = lazy(() =>
  import('./pages/ContractSettingsPage.js').then(({ ContractSettingsPage }) => ({
    default: ContractSettingsPage,
  })),
);
const BudgetImportSettingsPage = lazy(() =>
  import('./pages/BudgetImportSettingsPage.js').then(({ BudgetImportSettingsPage }) => ({
    default: BudgetImportSettingsPage,
  })),
);
const AccessAdminPage = lazy(() =>
  import('./pages/AccessAdminPage.js').then(({ AccessAdminPage }) => ({
    default: AccessAdminPage,
  })),
);
const ActionsPage = lazy(() =>
  import('./pages/ActionsPage.js').then(({ ActionsPage }) => ({ default: ActionsPage })),
);
const DataQualityPage = lazy(() =>
  import('./pages/DataQualityPage.js').then(({ DataQualityPage }) => ({
    default: DataQualityPage,
  })),
);
const HelpPage = lazy(() =>
  import('./pages/HelpPage.js').then(({ HelpPage }) => ({ default: HelpPage })),
);

export function App() {
  const { t } = useTranslation();
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api<Session>('/api/v1/auth/session', {}, sessionResponseSchema),
    retry: false,
  });
  if (session.isLoading) return <div className="app-loading">{t('shell.loading')}</div>;
  if (session.error)
    return (
      <div className="fatal-error">
        {t('shell.apiUnavailable')}
        <small>{session.error.message}</small>
      </div>
    );
  if (!session.data?.authenticated || !session.data.user)
    return <LoginPage configured={session.data?.googleConfigured ?? false} />;
  const access = session.data.access;
  const hasAny = (permission: PermissionKey) => access.anyScope.includes(permission);
  const hasGlobal = (permission: PermissionKey) => access.global.includes(permission);
  const canSeeActions = actionPermissions.some((permission) => hasAny(permission));
  const defaultPath =
    [
      ['portfolio.projects.read', '/projects'],
      ['capacity.read', '/capacity'],
      ['planning.read', '/planning'],
      ['budgets.read', '/budgets'],
      ['billing.read', '/billing'],
      ['governance.read', '/governance'],
    ].find(([permission]) => hasAny(permission as PermissionKey))?.[1] ??
    (hasGlobal('integrations.read') ? '/integrations' : null) ??
    (hasGlobal('settings.read') ? '/settings/clients' : null) ??
    (hasGlobal('admin.users.read') || hasGlobal('admin.roles.read') || hasGlobal('admin.teams.read')
      ? '/admin/access'
      : null);
  return (
    <AccessProvider access={access}>
      <Suspense
        fallback={
          <div className="app-loading" role="status" aria-live="polite">
            {t('shell.loading')}
          </div>
        }
      >
        <Routes>
          <Route
            element={
              <Layout
                user={session.data.user}
                features={
                  session.data.features ?? {
                    capacity: true,
                    insights: true,
                    finance: true,
                    agent: false,
                  }
                }
              />
            }
          >
            <Route
              index
              element={
                hasAny('budgets.read') ? (
                  <DashboardPage />
                ) : defaultPath ? (
                  <Navigate to={defaultPath} replace />
                ) : (
                  <NoAccessPage />
                )
              }
            />
            <Route
              path="actions"
              element={canSeeActions ? <ActionsPage /> : <Navigate to="/" replace />}
            />
            <Route path="help" element={<HelpPage />} />
            <Route
              path="production/squads/:squadId?"
              element={hasAny('capacity.read') ? <SquadsPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="settings/squads"
              element={
                hasAny('capacity.read') || hasGlobal('settings.manage') ? (
                  <SquadsSettingsPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="projects"
              element={
                hasAny('portfolio.projects.read') ? <ProjectsPage /> : <Navigate to="/" replace />
              }
            />
            <Route path="projects/:projectId/:tab?" element={<ProjectWorkspacePage />} />
            <Route
              path="resources"
              element={
                hasAny('capacity.read') && (session.data.features?.capacity ?? true) ? (
                  <ResourcesPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="resources/:resourceId"
              element={
                hasAny('capacity.read') && (session.data.features?.capacity ?? true) ? (
                  <ResourceProfilePage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="capacity"
              element={
                hasAny('capacity.read') && (session.data.features?.capacity ?? true) ? (
                  <CapacityCalendarPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="insights"
              element={
                hasAny('portfolio.projects.read') && (session.data.features?.insights ?? true) ? (
                  <InsightsPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="planning"
              element={hasAny('planning.read') ? <PlanningPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="budgets"
              element={hasAny('budgets.read') ? <BudgetsPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="billing"
              element={hasAny('billing.read') ? <BillingPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="financial-forecast"
              element={
                hasAny('financial.rates.read') && (session.data.features?.finance ?? true) ? (
                  <FinancialForecastPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="governance"
              element={hasAny('governance.read') ? <GovernancePage /> : <Navigate to="/" replace />}
            />
            <Route
              path="reports"
              element={hasAny('reports.read') ? <ReportsPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="integrations"
              element={
                hasGlobal('integrations.read') ? <IntegrationsPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="data-quality"
              element={
                hasAny('data.quality.read') ? <DataQualityPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="settings/clients"
              element={
                hasGlobal('settings.read') ? <SettingsClientsPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="settings/contracts"
              element={
                hasGlobal('settings.read') ? <ContractSettingsPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="settings/forecast"
              element={
                hasGlobal('settings.read') ? <ForecastSettingsPage /> : <Navigate to="/" replace />
              }
            />
            <Route
              path="settings/budget-imports"
              element={
                hasGlobal('settings.manage') ? (
                  <BudgetImportSettingsPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="settings/rates"
              element={
                (hasAny('financial.rates.read') || hasGlobal('financial.rates.manage')) &&
                (session.data.features?.finance ?? true) ? (
                  <FinancialRatesPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="admin/access"
              element={
                hasGlobal('admin.users.read') ||
                hasGlobal('admin.roles.read') ||
                hasGlobal('admin.teams.read') ? (
                  <AccessAdminPage />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </AccessProvider>
  );
}

function NoAccessPage() {
  const { t } = useTranslation();
  return (
    <div className="empty-state access-waiting">
      <h1>{t('shell.accessPending')}</h1>
      <p>{t('shell.accessPendingDescription')}</p>
    </div>
  );
}
